import { ConfirmDeleteDialog } from '@/components/app/confirm-delete-dialog';
import { ListPage } from '@/components/app/list-page';
import type { ListColumn, ListPageConfig } from '@/components/app/list-page';
import { useSidePanel } from '@/components/app/side-panel';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Switch } from '@/components/ui/switch';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  useDeleteAd,
  useDuplicateAd,
  useImportMetaAds,
  useListAds,
  usePauseAd,
  useResumeAd,
} from '@/features/meta-ads/api';
import type { Ad } from '@/features/meta-ads/api/types';
import { AdSidePanel } from '@/features/meta-ads/components/ad-side-panel';
import { useListCampaigns } from '@/features/meta-campaigns/api';
import type { Campaign } from '@/features/meta-campaigns/api/types';
import {
  Download,
  ExternalLink,
  Info,
  Loader2,
  Megaphone,
  MoreVertical,
  Rocket,
  Settings2,
} from 'lucide-react';
import * as React from 'react';
import { useCampaignAdInsights } from '../../api';
import {
  CORE_METRICS,
  CoreMetric,
  ResultCount,
} from '../campaign-list/campaign-metrics';
import { AdMetricsPanel } from '../campaign-list/campaign-metrics-panel';
import { getCampaignDestinationLabels } from '../campaign-list/campaign-presentation';
import { MetricLabel } from '../campaign-list/metric-label';
import { EditCampaignPanel } from '../edit-campaign-panel';
import { AdSyncError } from './ad-sync-error';
import { AdThumbnail } from './ad-thumbnail';
import { AdsPanelList } from './ads-panel-list';
import { LaunchAdDialog, canLaunchAd } from './launch-ad-dialog';

// ─── Delivery status mapping ────────────────────────────────────────────────

type DeliveryInfo = {
  label: string;
  dotColor: string;
};

function getDeliveryInfo(ad: Ad, campaign?: Campaign): DeliveryInfo {
  if (ad.status === 'draft') {
    return { label: 'In draft', dotColor: 'bg-gray-400' };
  }
  if (ad.status === 'launching') {
    return { label: 'Launching', dotColor: 'bg-blue-500 animate-pulse' };
  }
  if (ad.status === 'pending') {
    return { label: 'In review', dotColor: 'bg-yellow-500' };
  }
  if (campaign) {
    const campaignOff =
      campaign.effectiveStatus === 'PAUSED' ||
      campaign.effectiveStatus === 'CAMPAIGN_PAUSED' ||
      campaign.status === 'PAUSED';
    if (campaignOff && ad.status === 'active') {
      return { label: 'Campaign off', dotColor: 'bg-gray-400' };
    }
  }
  if (ad.status === 'active') {
    return { label: 'Active', dotColor: 'bg-green-500' };
  }
  if (ad.status === 'paused') {
    return { label: 'Off', dotColor: 'bg-gray-400' };
  }
  if (ad.status === 'rejected') {
    return { label: 'Rejected', dotColor: 'bg-red-500' };
  }
  if (ad.status === 'error') {
    return { label: 'Error', dotColor: 'bg-red-500' };
  }
  return { label: ad.status, dotColor: 'bg-gray-400' };
}

// ─── Actions (objective) mapping ────────────────────────────────────────────

const objectiveActionLabels: Record<string, string> = {
  OUTCOME_ENGAGEMENT: 'Messaging conversations',
  OUTCOME_LEADS: 'Leads',
  OUTCOME_SALES: 'Sales',
  OUTCOME_TRAFFIC: 'Link clicks',
  OUTCOME_AWARENESS: 'Impressions',
};

function getActionLabel(campaign?: Campaign): string | null {
  if (!campaign?.objective) return null;
  return objectiveActionLabels[campaign.objective] ?? null;
}

// ─── Row actions ────────────────────────────────────────────────────────────

function AdActions({
  ad,
  onEdit,
  onDuplicate,
  onLaunch,
}: {
  ad: Ad;
  onEdit: (ad: Ad) => void;
  onDuplicate: (ad: Ad) => void;
  onLaunch: (ad: Ad) => void;
}) {
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const { execute: deleteAd, isExecuting: isDeleting } = useDeleteAd();

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            aria-label={`Actions for ${ad.name}`}
            className="flex size-8 text-muted-foreground data-[state=open]:bg-muted"
            size="icon"
            variant="ghost"
          >
            <MoreVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          {canLaunchAd(ad) && (
            <DropdownMenuItem onSelect={() => onLaunch(ad)}>
              <Rocket className="mr-2 h-4 w-4" />
              {ad.status === 'error' ? 'Retry launch' : 'Launch ad'}
            </DropdownMenuItem>
          )}
          <DropdownMenuItem
            disabled={ad.status === 'rejected'}
            onSelect={() => onEdit(ad)}
          >
            Edit
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => onDuplicate(ad)}>
            Duplicate
          </DropdownMenuItem>
          {ad.metaAdId && (
            <DropdownMenuItem
              onSelect={() => {
                const url =
                  ad.metaPermalink ||
                  `https://www.facebook.com/ads/library/?id=${ad.metaAdId}`;
                window.open(url, '_blank', 'noopener,noreferrer');
              }}
            >
              <ExternalLink className="mr-2 h-4 w-4" />
              View on Facebook
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="text-destructive"
            onSelect={() => setDeleteDialogOpen(true)}
          >
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <ConfirmDeleteDialog
        description="This action cannot be undone. If the ad has been published to Meta, it will also be removed from your Meta Ads account."
        isPending={isDeleting}
        onConfirm={() => {
          deleteAd(ad.id, {
            onSuccess: () => setDeleteDialogOpen(false),
          });
        }}
        onOpenChange={setDeleteDialogOpen}
        open={deleteDialogOpen}
        title={<>Delete &ldquo;{ad.name}&rdquo;?</>}
      />
    </>
  );
}

// ─── Ad delivery toggle ────────────────────────────────────────────────────

function AdToggle({ ad }: { ad: Ad }) {
  const isOn = ad.status === 'active';
  const canToggle =
    !!ad.metaAdId && (ad.status === 'active' || ad.status === 'paused');
  const { execute: pauseAd, isExecuting: isPausing } = usePauseAd();
  const { execute: resumeAd, isExecuting: isResuming } = useResumeAd();
  const isBusy = isPausing || isResuming;

  const handleToggle = (checked: boolean) => {
    if (checked) {
      resumeAd(ad.id);
    } else {
      pauseAd(ad.id);
    }
  };

  if (!canToggle) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              onClick={(event) => event.stopPropagation()}
              onKeyDown={(event) => event.stopPropagation()}
            >
              <Switch
                checked={isOn}
                aria-readonly
                aria-label={isOn ? 'Ad is delivering' : 'Ad is not delivering'}
                tabIndex={-1}
                className="pointer-events-none opacity-70"
              />
            </span>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs">
            <p>
              {ad.status === 'draft'
                ? 'Publish this ad before toggling delivery.'
                : 'This ad cannot be toggled right now.'}
            </p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => event.stopPropagation()}
          >
            <Switch
              checked={isOn}
              disabled={isBusy}
              aria-label={isOn ? 'Pause ad delivery' : 'Resume ad delivery'}
              onCheckedChange={handleToggle}
            />
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          <p>{isOn ? 'Click to pause this ad.' : 'Click to resume this ad.'}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// ─── Main list ──────────────────────────────────────────────────────────────

interface AdsTableProps {
  campaignId: string;
  onNewAd?: () => void;
  panel?: boolean;
  onAdSelect?: (ad: Ad) => void;
  /**
   * Hide write controls (on/off toggle, row actions, Edit Campaign, Import
   * from Meta, New Ad). Used by the admin panel, where cross-org writes are
   * blocked by org-membership guards.
   */
  readOnly?: boolean;
  initialEditOpen?: boolean;
}

/**
 * The ads inside one campaign, on the shared `ListPage`.
 *
 * This IS the campaign-detail surface: the shell supplies the page header (the
 * campaign name), the search row, the New Ad action and — from the SAME column
 * config — the phone rows that `CampaignMobileDetail` used to hand-write beside
 * this table.
 */
export function AdsTable({
  campaignId,
  onNewAd,
  panel = false,
  onAdSelect,
  readOnly = false,
  initialEditOpen = false,
}: AdsTableProps) {
  const [search, setSearch] = React.useState('');
  const [editingCampaign, setEditingCampaign] = React.useState(initialEditOpen);
  const [launchingAd, setLaunchingAd] = React.useState<Ad | null>(null);

  const { open: openPanel, close: closePanel } = useSidePanel();

  const { ads, isLoading, isError, error, refetch } = useListAds({
    metaCampaignId: campaignId,
  });

  const { campaigns } = useListCampaigns();
  const campaign = campaigns.find((c) => c.id === campaignId);

  const { execute: duplicateAd } = useDuplicateAd();
  const { importAds, isImporting } = useImportMetaAds();

  // KEYED BY AD ID, and that is not cosmetic.
  //
  // `openPanel` does `setContent(node)` and the host renders `{content}`
  // unkeyed at a fixed position (`components/app/side-panel.tsx`), so swapping
  // one `<AdSidePanel>` for another reconciles as the SAME element: React
  // updates it in place and never remounts. Everything inside then survives
  // the switch — `useForm`'s `defaultValues` only apply on mount, so the panel
  // kept showing the PREVIOUS ad's copy under the new ad's name, and since the
  // creative picker landed it would also keep a STAGED creative. Open row A,
  // stage an image, click row B, save: A's media onto B.
  //
  // The key forces a remount per ad, which is what "open this ad" means.
  const handleEditAd = React.useCallback(
    (ad: Ad) => {
      if (onAdSelect) onAdSelect(ad);
      else
        openPanel(<AdSidePanel key={ad.id} ad={ad} />, {
          presentation: 'overlay',
        });
    },
    [openPanel, onAdSelect]
  );

  const handleDuplicateAd = React.useCallback(
    (ad: Ad) => {
      // Launched ads (incl. imported/graphic) are copied server-side on Meta;
      // drafts are recreated as a fresh draft. Both handled by the API.
      duplicateAd(ad.id);
    },
    [duplicateAd]
  );

  // Filtering lives in the page, not the shell — see the list-page README.
  const rows = React.useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return ads;
    return ads.filter((ad) => ad.name.toLowerCase().includes(term));
  }, [ads, search]);

  const actionLabel = getActionLabel(campaign);
  // Lifetime per-ad metrics: one Meta request for every ad in the campaign.
  const {
    totalsFor,
    isLoading: isInsightsLoading,
    isError: insightsFailed,
    currency: insightsCurrency,
  } = useCampaignAdInsights(campaignId);
  // Meta's own currency for these figures; the campaign's stored one is the
  // fallback for older API responses.
  const currency = insightsCurrency ?? campaign?.currency;

  const columns: ListColumn<Ad>[] = [
    {
      id: 'thumbnail',
      mobile: 'media',
      width: 'w-14',
      cell: (ad) => <AdThumbnail ad={ad} large={panel} />,
    },
    {
      id: 'toggle',
      header: 'Off/On',
      width: 'w-20',
      hidden: readOnly,
      cell: (ad) => <AdToggle ad={ad} />,
    },
    {
      id: 'name',
      header: 'Ad',
      mobile: 'primary',
      // Capped so one long ad name cannot push the row actions off-screen.
      cell: (ad) => (
        <div className="flex min-w-0 max-w-64 items-center gap-2">
          <span className="truncate font-medium" title={ad.name}>
            {ad.name}
          </span>
          {ad.isImported && (
            <span className="inline-flex shrink-0 items-center rounded-full bg-blue-50 px-1.5 py-0.5 font-medium text-[10px] text-blue-700 ring-1 ring-blue-600/20 ring-inset dark:bg-blue-900/30 dark:text-blue-300 dark:ring-blue-500/30">
              Imported
            </span>
          )}
        </div>
      ),
    },
    {
      id: 'delivery',
      header: 'Delivery',
      // Whether this ad is running is the line that belongs under its name on a
      // phone.
      mobile: 'secondary',
      cell: (ad) => {
        const delivery = getDeliveryInfo(ad, campaign);
        return (
          <div className="flex items-center gap-2 whitespace-nowrap">
            <span
              className={`size-2 shrink-0 rounded-full ${delivery.dotColor}`}
            />
            {ad.syncError ? (
              <AdSyncError
                message={ad.syncError}
                label={ad.syncError.split(/\n|: /, 1)[0]}
                metaAdId={ad.metaAdId}
                onRetry={
                  !readOnly && canLaunchAd(ad)
                    ? () => setLaunchingAd(ad)
                    : undefined
                }
              />
            ) : (
              <span className="text-sm">{delivery.label}</span>
            )}
          </div>
        );
      },
    },
    {
      id: 'actionsType',
      header: 'Actions',
      cell: () =>
        actionLabel ? (
          <span className="block max-w-[160px] truncate text-muted-foreground text-sm">
            {actionLabel}
          </span>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    // The old phone row's result. Desktop shows the metric columns.
    {
      id: 'mobileResults',
      mobile: 'trailing',
      mobileOnly: true,
      cell: (ad) =>
        campaign ? (
          <ResultCount
            campaign={campaign}
            totals={totalsFor(ad.metaAdId)}
            loading={isInsightsLoading}
            failed={insightsFailed}
          />
        ) : null,
    },
    ...CORE_METRICS.map(
      ({ id, label }): ListColumn<Ad> => ({
        id,
        header: <MetricLabel label={label} />,
        align: 'right',
        cell: (ad) => (
          <CoreMetric
            id={id}
            totals={totalsFor(ad.metaAdId)}
            currency={currency}
            loading={isInsightsLoading}
            failed={insightsFailed}
          />
        ),
      })
    ),
  ];

  const config: ListPageConfig<Ad> = {
    title: campaign?.name ?? 'Campaign',
    stickyHeader: true,
    description: 'Select an ad to edit its creative and destination settings.',
    columns,
    rows,
    rowKey: (ad) => ad.id,
    // Keyed per ad — see handleEditAd for why.
    onRowClick: handleEditAd,
    rowActions: (ad) => (
      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="sm"
          className={`text-xs text-muted-foreground ${panel ? 'hidden' : 'max-md:hidden'}`}
          onClick={(event) => {
            event.stopPropagation();
            openPanel(
              <AdMetricsPanel
                key={`${ad.id}-metrics`}
                campaignId={campaignId}
                metaAdId={ad.metaAdId}
                adName={ad.name}
                currency={currency}
              />,
              { presentation: 'overlay' }
            );
          }}
        >
          More metrics
        </Button>
        {!readOnly && panel && <AdToggle ad={ad} />}
        {!readOnly && (
          <AdActions
            ad={ad}
            onDuplicate={handleDuplicateAd}
            onEdit={handleEditAd}
            onLaunch={setLaunchingAd}
          />
        )}
      </div>
    ),
    searchPlaceholder: 'Search ads',
    search,
    onSearchChange: setSearch,
    toolbar: readOnly ? undefined : (
      <>
        <Button
          disabled={!campaign}
          onClick={() => setEditingCampaign(true)}
          size="sm"
          variant="outline"
        >
          {panel && <Settings2 aria-hidden className="size-4" />}
          Edit Campaign
        </Button>
        <Button
          aria-label="Import from Meta"
          disabled={isImporting}
          onClick={() => importAds()}
          size="sm"
          variant="outline"
        >
          {isImporting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Download className="h-4 w-4" />
          )}
          <span className={panel ? undefined : 'hidden lg:inline'}>
            {isImporting
              ? 'Importing...'
              : panel
                ? 'Import'
                : 'Import from Meta'}
          </span>
        </Button>
      </>
    ),
    primaryAction:
      readOnly || !campaign?.followUpType
        ? undefined
        : {
            label: 'New Ad',
            mobileLabel: 'New',
            onClick: () => onNewAd?.(),
          },
    isLoading,
    isError,
    errorMessage: `Failed to load ads: ${error?.message || 'Unknown error'}`,
    onRetry: () => void refetch(),
    footer: rows.length > 0 && (
      <div className="mt-4 flex shrink-0 items-center gap-1 border-t border-border/60 pt-4 text-muted-foreground text-sm">
        Results from {rows.length} {rows.length === 1 ? 'ad' : 'ads'}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger>
              <Info className="h-3.5 w-3.5" />
            </TooltipTrigger>
            <TooltipContent>
              <p>Aggregated results from all ads in this campaign</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    ),
    empty: {
      icon: Megaphone,
      title: search ? 'No matching ads' : 'No ads yet',
      description: search
        ? 'No ad in this campaign matches that search.'
        : campaign?.followUpType
          ? 'Create an ad to start reaching your audience.'
          : 'Create a campaign in Borrah to run your ads.',
    },
  };
  if (editingCampaign && campaign && !readOnly) {
    return (
      <EditCampaignPanel
        campaign={campaign}
        onBack={() => setEditingCampaign(false)}
        onClose={panel ? closePanel : () => setEditingCampaign(false)}
      />
    );
  }

  return (
    <>
      {panel ? (
        <AdsPanelList
          config={config}
          destinations={campaign ? getCampaignDestinationLabels(campaign) : []}
        />
      ) : (
        <ListPage<Ad> config={config} />
      )}
      {launchingAd && (
        <LaunchAdDialog
          ad={launchingAd}
          open
          onOpenChange={(open) => {
            if (!open) setLaunchingAd(null);
          }}
        />
      )}
    </>
  );
}
